import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const SUB_PROCESSORS = [
  {
    name: 'Recall.ai',
    purpose: 'Meeting bot joining + native live transcription for Google Meet, Zoom, and Microsoft Teams calls.',
    data: 'Meeting audio (transient), meeting transcripts, participant names visible in the meeting.',
    region: 'EU (Frankfurt) — eu-central-1',
    dpa: 'https://www.recall.ai/legal/dpa',
  },
  {
    name: 'OpenAI',
    purpose: 'GPT-5.2 powers live qualification scoring, suggested questions, and post-call summaries.',
    data: 'Transcript text (not audio). API requests opt out of training per OpenAI Enterprise terms.',
    region: 'United States — multi-region',
    dpa: 'https://openai.com/policies/data-processing-addendum',
  },
  {
    name: 'Deepgram',
    purpose: 'Speech-to-text for in-person / phone-call mode (browser microphone input only).',
    data: 'Audio chunks streamed during a live call (not retained).',
    region: 'United States',
    dpa: 'https://deepgram.com/privacy',
  },
  {
    name: 'Paystack',
    purpose: 'Subscription billing, payment processing, invoice generation.',
    data: 'Billing email, organisation name, payment method tokens (cards never touch our servers).',
    region: 'South Africa + Nigeria (PCI-DSS certified)',
    dpa: 'https://paystack.com/terms',
  },
  {
    name: 'Resend',
    purpose: 'Transactional email delivery (welcome, invites, weekly digests, billing receipts).',
    data: 'Recipient email address, email content.',
    region: 'United States',
    dpa: 'https://resend.com/legal/dpa',
  },
  {
    name: 'Google (OAuth)',
    purpose: 'Optional "Sign in with Google" authentication.',
    data: 'Email, name, profile picture — only if the user chooses Google sign-in.',
    region: 'Multi-region',
    dpa: 'https://cloud.google.com/terms/data-processing-addendum',
  },
  {
    name: 'MongoDB Atlas',
    purpose: 'Primary application database — call sessions, users, organisations, settings.',
    data: 'All PipeGuide application data (transcripts, scores, frameworks, products, billing records).',
    region: 'AWS af-south-1 (Cape Town) — data residency for African customers',
    dpa: 'https://www.mongodb.com/legal/data-protection-agreement',
  },
  {
    name: 'Emergent (hosting platform)',
    purpose: 'Application hosting, deployment, log infrastructure.',
    data: 'Server logs (no PII in logs by policy), encrypted application files.',
    region: 'Multi-region cloud',
    dpa: 'https://emergent.sh/legal',
  },
];

export default function SubProcessorsPage() {
  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-brand-primary text-white px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-sm opacity-80 hover:opacity-100 flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to app
          </Link>
          <div className="text-sm font-bold tracking-wide">Sub-processors</div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
          Our sub-processors
        </h1>
        <p className="text-zinc-600 mb-8 max-w-2xl">
          PipeGuide uses a small number of trusted third-party services to deliver the product.
          Each is bound by a Data Processing Agreement (DPA) and is listed here for full transparency,
          as required by POPIA §21 and GDPR Article 28.
        </p>

        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="px-5 py-3">Provider</th>
                <th className="px-5 py-3">Purpose &amp; data processed</th>
                <th className="px-5 py-3">Region</th>
                <th className="px-5 py-3">DPA</th>
              </tr>
            </thead>
            <tbody>
              {SUB_PROCESSORS.map((sp, i) => (
                <tr key={sp.name} className={i > 0 ? 'border-t border-zinc-100' : ''} data-testid={`sub-processor-${sp.name.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                  <td className="px-5 py-4 align-top font-semibold text-zinc-900">{sp.name}</td>
                  <td className="px-5 py-4 align-top text-zinc-700">
                    <div>{sp.purpose}</div>
                    <div className="text-xs text-zinc-500 mt-1">{sp.data}</div>
                  </td>
                  <td className="px-5 py-4 align-top text-zinc-700 text-xs">{sp.region}</td>
                  <td className="px-5 py-4 align-top">
                    <a
                      href={sp.dpa}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-primary hover:underline text-xs inline-flex items-center gap-1"
                    >
                      View DPA <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-8 bg-brand-tint border border-brand-accent rounded-xl p-5 text-sm text-zinc-700">
          <h3 className="font-bold text-brand-primary mb-2">Changes to this list</h3>
          <p>
            We'll notify customers via email at least <strong>30 days</strong> before adding any new
            sub-processor or making a material change to data flow. To object, email{' '}
            <a href="mailto:privacy@pipeguide.tech" className="text-brand-primary font-semibold">
              privacy@pipeguide.tech
            </a>.
          </p>
        </div>

        <p className="text-xs text-zinc-500 mt-6 text-center">
          Last updated: 14 Feb 2026 &middot; Questions? <a href="mailto:privacy@pipeguide.tech" className="text-brand-primary">privacy@pipeguide.tech</a>
        </p>
      </main>
    </div>
  );
}
